/**
 * Reads what .NET's BinaryWriter wrote: the legacy grid wire format (`binMatrix`, `footerMatrix`).
 *
 * Every value comes out identical to the reader this replaced, which allocated a fresh
 * `Uint8Array` for every byte it read (`slice` per field), went through BigInt for every int64 and
 * decimal, and decoded strings with `escape` + `decodeURIComponent`. This one reads in place
 * through a `DataView` and decodes strings with `TextDecoder` — about 3.5x faster on a 5,000-row
 * grid. The places where "identical" took care are commented below.
 */

/** 10^n as a double, n = 0..28, computed exactly as the old reader did: Number(10n ** n). Above
 * 10^22 a power of ten is not representable, and dividing by a different rounding of it would
 * change the last bit of a high-scale decimal. */
const POW10: number[] = Array.from({ length: 29 }, (_, n) => Number(10n ** BigInt(n)));
const TWO32 = 4294967296;
const TWO53 = 9007199254740992;

/** Keeps a leading U+FEFF, as the old decode did; a default TextDecoder silently drops it. */
const utf8 = new TextDecoder("utf-8", { ignoreBOM: true });

/** Base64 to bytes. Used for every legacy row and for version 2 `bytes` cells. */
export function fromBase64String(data: string): Uint8Array {
  const raw = atob(data);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

export class BinaryReader {
  private _data: Uint8Array;
  private _view: DataView;
  position: number = 0;
  length: number = 0;

  constructor(data: string | Uint8Array) {
    this._data = typeof data === "string" ? fromBase64String(data) : data;
    this._view = new DataView(this._data.buffer, this._data.byteOffset, this._data.byteLength);
    this.length = this._data.length;
  }

  hasData(): boolean {
    return this.position < this.length;
  }

  skip(size: number): void {
    this.position += size;
  }

  readBoolean(): boolean {
    return this.readByte() !== 0;
  }

  readByte(): number {
    return this._data[this.position++]!;
  }

  /** A copy, as before: a caller holding a ByteArr cell must not be holding the whole row. */
  readBytes(size: number): Uint8Array {
    const out = this._data.slice(this.position, this.position + size);
    this.position += size;
    return out;
  }

  readInt16(): number {
    const v = this._view.getInt16(this.position, true);
    this.position += 2;
    return v;
  }

  readUInt16(): number {
    const v = this._view.getUint16(this.position, true);
    this.position += 2;
    return v;
  }

  readInt32(): number {
    const v = this._view.getInt32(this.position, true);
    this.position += 4;
    return v;
  }

  readUInt32(): number {
    const v = this._view.getUint32(this.position, true);
    this.position += 4;
    return v;
  }

  /** hi * 2^32 is exact, so the one rounding is the addition — the same single round-to-nearest
   * that Number(BigInt) performed. Identical for every int64, not only those under 2^53. */
  readInt64(): number {
    const lo = this._view.getUint32(this.position, true);
    const hi = this._view.getInt32(this.position + 4, true);
    this.position += 8;
    return hi * TWO32 + lo;
  }

  /**
   * A .NET DateTime's ticks as JavaScript epoch milliseconds, exactly: the integer division the
   * server's version 2 encoder does, truncating toward zero as `new Date()` does.
   * <p>Not `(readInt64() - epoch) / 10000`. Ticks for any date this century are around 6.4e17,
   * far past 2^53, so as a double they are rounded to a multiple of 128 before the subtraction —
   * and a time landing just under a millisecond boundary then truncates to the millisecond
   * before. 08:30:15.123 read back as 08:30:15.122.</p>
   */
  readTicksAsUnixMs(): number {
    const lo = this._view.getUint32(this.position, true);
    const hi = this._view.getInt32(this.position + 4, true);
    this.position += 8;
    return Number((BigInt(hi) * 4294967296n + BigInt(lo) - 621355968000000000n) / 10000n);
  }

  readUInt64(): number {
    const lo = this._view.getUint32(this.position, true);
    const hi = this._view.getUint32(this.position + 4, true);
    this.position += 8;
    return hi * TWO32 + lo;
  }

  readSingle(): number {
    const v = this._view.getFloat32(this.position, true);
    this.position += 4;
    return v;
  }

  readDouble(): number {
    const v = this._view.getFloat64(this.position, true);
    this.position += 8;
    return v;
  }

  /**
   * A .NET decimal: a 96-bit integer and a power-of-ten scale. Returned as a double, as it always
   * has been in WebAdminCC — the version 2 format carries the exact text for anything that needs
   * it. Money-sized values (under 2^53) take the fast path, which is exact; anything wider goes
   * through BigInt exactly as the old reader did, so the double is the same one.
   */
  readDecimal(): number {
    const p = this.position;
    this.position += 16;
    const lo = this._view.getUint32(p, true);
    const mid = this._view.getUint32(p + 4, true);
    const hi = this._view.getUint32(p + 8, true);
    const flags = this._view.getUint32(p + 12, true);
    const scale = (flags >>> 16) & 0xff;

    const small = mid * TWO32 + lo;
    const raw = hi === 0 && small < TWO53
      ? small
      : Number(BigInt(lo) + BigInt(mid) * 4294967296n + BigInt(hi) * 18446744073709551616n);
    const result = raw / POW10[scale]!;
    return (flags & 0x80000000) !== 0 ? -result : result;
  }

  readString(): string {
    let shift = 0;
    let length = 0;
    for (;;) {
      const b = this._data[this.position++]!;
      length |= (b & 0x7f) << shift;
      shift += 7;
      if ((b & 0x80) === 0) break;
    }
    const s = utf8.decode(this._data.subarray(this.position, this.position + length));
    this.position += length;
    return s;
  }

  readGuid(): string {
    // Guid.ToByteArray(): the first three groups are little-endian, the last two are byte order.
    const b = this._data;
    const p = this.position;
    this.position += 16;
    const h = (i: number) => b[p + i]!.toString(16).padStart(2, "0");
    return (
      h(3) + h(2) + h(1) + h(0) + "-" +
      h(5) + h(4) + "-" +
      h(7) + h(6) + "-" +
      h(8) + h(9) + "-" +
      h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
    );
  }
}
